import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { DescriptorTable } from '../../descriptor/descriptor-table';
import { UrlConstructor } from '../../util/url-constructor';
import { DescriptorAction } from '../../descriptor/descriptor-action';
import { VedraxTableComponent } from '../vedrax-table/vedrax-table.component';
import { ActionType } from '../../enum/action-types';
import { FormDescriptorService } from '../../services/form-descriptor.service';
import { DownloadService } from '../../services/download.service';
import { VedraxApiService } from '../../services/vedrax-api.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TableSelectionItem } from '../../shared/table-selection-item';

/**
 * Class that represents a CRUD component
 */
@Component({
  selector: 'vedrax-crud',
  templateUrl: './vedrax-crud.component.html',
  styleUrls: ['./vedrax-crud.component.scss']
})
export class VedraxCrudComponent implements OnInit, OnDestroy {

  /**
   * the component title
   */
  @Input() title: string = '';

  /**
   * an optional back url
   */
  @Input() backUrl?: string;

  /**
   * an optional description
   */
  @Input() description?: string;

  /**
   * a table descriptor
   */
  @Input() tableDescriptor: DescriptorTable = new DescriptorTable();

  /**
   * an optionnal action descriptor for the create button
   */
  @Input() createAction?: DescriptorAction;

  /**
   * The paginator
   */
  private tableComponent?: VedraxTableComponent;

  /**
   * table component reference
   */
  @ViewChild(VedraxTableComponent) set table(table: VedraxTableComponent) {
    this.tableComponent = table;
  }

  /**
  * The list of subscription
  */
  private subscription: Subscription = new Subscription();

  constructor(
    private formDescriptorService: FormDescriptorService,
    private vedraxApiService: VedraxApiService,
    private downloadService: DownloadService,
    private snackBarService: SnackbarService,
    private router: Router) { }

  ngOnInit(): void {
  }

  /**
   * Used for unsubscribing on destroy
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Method used to trigger the creation form
   */
  create() {
    if (this.createAction) {
      this.openFormDialog(this.createAction.url, true);
    }
  }

  /**
  * Method for selecting an item in the table
  * 
  * @param element the selected element
  */
  select(selection: TableSelectionItem = new TableSelectionItem()): void {

    const action: DescriptorAction = selection.action || new DescriptorAction();
    const item: any = selection.item;

    const endpoint: string = new UrlConstructor()
      .setFragment(action.url)
      .setFragment(`${item['id']}`)
      .setFragment(action.fragment)
      .build();

    if (action.action === ActionType.redirect) {
      this.redirectToUrl(endpoint, item);
      return;
    }

    if (action.action === ActionType.form) {
      this.openFormDialog(endpoint, false);
      return;
    }

    //download file with Content-Disposition attachment header
    if (action.action === ActionType.download) {
      this.downloadDocument(action.url, item['id'], action.params);
    }

    //call directly CRON JOB
    if (action.action === ActionType.job) {
      this.initJobOnServer(endpoint, action.params);
    }
  }

  /**
   * Method used to initiate a cron job
   * @param endpoint 
   * @param params 
   */
  private initJobOnServer(endpoint: string, params: Map<string, any> = new Map()): void {
    const query = this.vedraxApiService.queryMapToHttpParams(params);
    this.subscription.add(
      this.vedraxApiService.get(endpoint, query).subscribe(
        vo => {
          if (this.tableComponent) {
            this.tableComponent.updateItem(vo);
            this.snackBarService.open('Init. Job...');
          }
        }));
  }

  /**
   * Method used to download a document
   * @param endpoint 
   * @param id 
   * @param params 
   */
  private downloadDocument(endpoint: string = '', id: string | number = '', params: Map<string, any> = new Map()): void {
    //add id as a query parameter
    params = params.set('id', id);
    const query = this.vedraxApiService.queryMapToHttpParams(params);
    this.subscription.add(
      this.downloadService.download(endpoint, query)
        .subscribe(data => this.downloadFile(data, id, params)));
  }

  /**
   * Method used to download a file
   * @param data 
   * @param id 
   * @param params 
   */
  private downloadFile(data: any, id: string | number, params: Map<string, any> = new Map()) {
    const blob = new Blob([data], { type: `application/${params.get('extension')}` });
    const url = window.URL.createObjectURL(blob);
    let a = document.createElement("a");
    a.href = url;
    a.download = `${params.get('type')}${id}.${params.get('extension')}`;
    // start download
    a.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Method used to open dialog for creation and update
   * @param endpoint 
   * @param isForCreate 
   */
  private openFormDialog(endpoint: string = '', isForCreate: boolean = false): void {

    this.subscription.add(
      this.formDescriptorService.getDescriptor(endpoint, isForCreate)
        .subscribe(descriptor => {
          if (descriptor && this.tableComponent) {
            this.tableComponent.openDialog(descriptor);
          }
        }));
  }

  /**
   * Method for returning to the providing URL
   * @param action the provided action object for which the URL is constructed
   * @param item the provided selected item
   */
  private redirectToUrl(endpoint: string, item: any): void {
    this.router.navigateByUrl(endpoint, { state: item });
  }

}
